import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, TextField, Chip, IconButton, Alert, Grid,
  Tabs, Tab, Divider, Avatar
} from '@mui/material';
import { Person as PersonIcon, Security as SecurityIcon, Delete as DeleteIcon, Add as AddIcon, Cloud as CloudIcon, AccountBalance as BankIcon, Payments as SalaryIcon, Edit as EditIcon, Settings as SettingsIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { isAdminEmail } from '../../config/admin';
import AnimatedLoader from '../../components/common/AnimatedLoader';
import { awsUserService } from '../../services/aws-user-service';
import { CompanySettings, ModuleAccessLevel, ModuleAccess } from '../../types/hr';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

// Define the valid roles (legacy fallback)
const VALID_ROLES = {
  admin: 'Admin',
  accountant: 'Accountant',
  cashier: 'Cashier',
  viewer: 'Viewer',
  manager: 'Manager'
};

const MODULES: (keyof ModuleAccess)[] = [
  'dashboard', 'pos', 'inventory', 'expenses', 'finance', 'reports', 'payroll', 'leaves', 'staff', 'settings'
];

const StaffManagement: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [tabValue, setTabValue] = useState(0);

  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('cashier');

  // HR Verification Details State
  const [openHRDialog, setOpenHRDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [hrDetails, setHrDetails] = useState<any>({});
  const [moduleAccess, setModuleAccess] = useState<ModuleAccess>({
    dashboard: 'none', pos: 'none', inventory: 'none', expenses: 'none', finance: 'none',
    reports: 'none', payroll: 'none', leaves: 'none', staff: 'none', settings: 'none'
  });
  const [savingHR, setSavingHR] = useState(false);

  // Global HR Settings State
  const [openGlobalHRDialog, setOpenGlobalHRDialog] = useState(false);
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    casual_leaves_per_year: 12, sick_leaves_per_year: 12, weekends_off: ['Saturday', 'Sunday'], company_holidays: [],
  });
  const [savingGlobalHR, setSavingGlobalHR] = useState(false);
  const [newHoliday, setNewHoliday] = useState<Date | null>(null);
  const [newHolidayName, setNewHolidayName] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const tenantId = userProfile?.tenantId || currentUser?.uid;
      if (!tenantId) return;

      const response = await fetch(`http://localhost:5000/api/staff?tenantId=${tenantId}`);
      if (!response.ok) throw new Error('Failed to fetch staff members');
      const data = await response.json();
      
      try {
        const ownerProfile = await awsUserService.getUserProfile(tenantId);
        if (ownerProfile && ownerProfile.hrSettings) setCompanySettings(ownerProfile.hrSettings);
      } catch (e) { console.warn('Could not load owner HR settings:', e); }
      
      if (data.success && data.users) {
        const formattedUsers = await Promise.all(data.users.map(async (cognitoUser: any) => {
          const attributes = cognitoUser.Attributes.reduce((acc: any, attr: any) => {
            acc[attr.Name] = attr.Value;
            return acc;
          }, {});

          const uid = cognitoUser.Username;
          const email = attributes['email'];
          
          let fetchedHrDetails = {};
          try {
            const profile = await awsUserService.getUserProfile(uid);
            if (profile && profile.hrDetails) fetchedHrDetails = profile.hrDetails;
          } catch (err) { console.warn(`Could not fetch HR details for ${email}:`, err); }

          return {
            uid,
            email,
            displayName: attributes['name'] || email?.split('@')[0],
            role: attributes['custom:role'],
            phone: attributes['phone_number'] || '',
            status: cognitoUser.UserStatus === 'FORCE_CHANGE_PASSWORD' ? 'invited' : (cognitoUser.Enabled ? 'active' : 'disabled'),
            enabled: cognitoUser.Enabled,
            hrDetails: fetchedHrDetails
          };
        }));

        setUsers(formattedUsers.filter(u => u.enabled));
      } else {
        setUsers([]);
      }
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  const pendingApprovals = users.filter(u => u.hrDetails?.onboarding_status === 'pending_hr_approval');
  const activeStaff = users.filter(u => u.hrDetails?.onboarding_status !== 'pending_hr_approval');

  const handleDeleteUser = async (user: any) => {
    if (window.confirm(`Are you sure you want to remove ${user.displayName || user.email}? This will instantly revoke their cloud access.`)) {
      try {
        setLoading(true);
        const response = await fetch(`http://localhost:5000/api/staff/remove`, {
          method: 'DELETE', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: user.uid })
        });
        if (!response.ok) throw new Error((await response.json()).error || 'Failed to remove user');
        setSuccess(`User ${user.displayName || user.email} removed and access revoked.`);
        fetchUsers();
      } catch (err: any) {
        setError(err.message || 'Failed to remove user.');
      } finally { setLoading(false); }
    }
  };

  const handleInviteSubmit = async () => {
    if (!inviteEmail) return;
    try {
      setLoading(true);
      const tenantId = userProfile?.tenantId || currentUser?.uid;
      if (!tenantId) throw new Error("No current user");
      
      const response = await fetch(`http://localhost:5000/api/staff/invite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole, tenantId })
      });
      if (!response.ok) throw new Error((await response.json()).error || 'Failed to invite staff');
      
      setSuccess(`Invite sent successfully to ${inviteEmail}.`);
      setOpenInviteDialog(false); setInviteEmail(''); fetchUsers(); 
    } catch (err: any) { setError(err.message || 'Failed to create invite.'); } 
    finally { setLoading(false); }
  };

  const handleOpenHRDialog = (user: any) => {
    setSelectedUser(user);
    setHrDetails(user.hrDetails || {});
    setModuleAccess(user.hrDetails?.module_access || {
      dashboard: 'none', pos: 'none', inventory: 'none', expenses: 'none', finance: 'none',
      reports: 'none', payroll: 'none', leaves: 'none', staff: 'none', settings: 'none'
    });
    setOpenHRDialog(true);
  };

  const generateEmployeeId = () => {
    const businessName = userProfile?.businessName || 'AKM';
    const prefix = businessName.substring(0, 3).toUpperCase();
    const date = new Date();
    const yymm = `${String(date.getFullYear()).substring(2,4)}${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    // Find highest number for this prefix-yymm
    let maxNum = 0;
    users.forEach(u => {
      if (u.hrDetails?.employee_id?.startsWith(`${prefix}-${yymm}-`)) {
        const num = parseInt(u.hrDetails.employee_id.split('-')[2]);
        if (num > maxNum) maxNum = num;
      }
    });
    
    return `${prefix}-${yymm}-${String(maxNum + 1).padStart(3, '0')}`;
  };

  const handleSaveHRDetails = async () => {
    if (!selectedUser) return;
    try {
      setSavingHR(true);
      
      const isApproving = selectedUser.hrDetails?.onboarding_status === 'pending_hr_approval';
      let empId = hrDetails.employee_id;
      
      if (isApproving && !empId) {
        empId = generateEmployeeId();
      }

      const profile = await awsUserService.getUserProfile(selectedUser.uid) || {
        userId: selectedUser.uid, email: selectedUser.email, displayName: selectedUser.displayName,
        role: selectedUser.role, tenantId: userProfile?.tenantId || currentUser?.uid
      };

      const updatedProfile = {
        ...profile,
        hrDetails: {
          ...hrDetails,
          employee_id: empId,
          module_access: moduleAccess,
          onboarding_status: 'active'
        }
      };

      await awsUserService.saveUserProfile(updatedProfile);
      
      setSuccess(`Details updated for ${selectedUser.displayName || selectedUser.email}. ID: ${empId}`);
      setOpenHRDialog(false);
      fetchUsers();
    } catch (err: any) { setError(err.message || 'Failed to save details.'); } 
    finally { setSavingHR(false); }
  };

  const handleModuleAccessChange = (module: keyof ModuleAccess, level: ModuleAccessLevel) => {
    setModuleAccess(prev => ({ ...prev, [module]: level }));
  };

  const handleSaveGlobalHR = async () => {
    try {
      setSavingGlobalHR(true);
      const tenantId = userProfile?.tenantId || currentUser?.uid;
      if (!tenantId) throw new Error("No tenant ID found");
      const ownerProfile = await awsUserService.getUserProfile(tenantId);
      if (ownerProfile) await awsUserService.updateUserProfile(tenantId, { hrSettings: companySettings });
      else await awsUserService.saveUserProfile({ userId: tenantId, email: currentUser?.email, role: 'admin', hrSettings: companySettings });
      setSuccess('Global HR Settings saved.'); setOpenGlobalHRDialog(false);
    } catch (err: any) { setError(err.message || 'Failed to save Global HR settings.'); } 
    finally { setSavingGlobalHR(false); }
  };

  const handleAddHoliday = () => {
    if (newHoliday && newHolidayName.trim() !== '') {
      const dateStr = newHoliday.toISOString().split('T')[0];
      if (!companySettings.company_holidays.find(h => h.date === dateStr)) {
        setCompanySettings(prev => ({ ...prev, company_holidays: [...prev.company_holidays, { date: dateStr, name: newHolidayName.trim() }].sort((a, b) => a.date.localeCompare(b.date)) }));
      }
      setNewHoliday(null); setNewHolidayName('');
    }
  };

  const handleRemoveHoliday = (dateStr: string) => {
    setCompanySettings(prev => ({
      ...prev,
      company_holidays: prev.company_holidays.filter(h => h.date !== dateStr)
    }));
  };

  const handleToggleWeekend = (day: string) => {
    setCompanySettings(prev => ({ ...prev, weekends_off: prev.weekends_off.includes(day) ? prev.weekends_off.filter(d => d !== day) : [...prev.weekends_off, day] }));
  };

  const isAuthorized = userProfile?.role === 'admin' || userProfile?.role === 'super_admin' || isAdminEmail(currentUser?.email || '');
  if (!isAuthorized) return <Container maxWidth="lg" sx={{ mt: 4 }}><Alert severity="error">You do not have permission to access the Staff Management page.</Alert></Container>;
  if (loading && users.length === 0) return <AnimatedLoader message="Loading staff records..." />;

  const renderTable = (userList: any[]) => (
    <TableContainer sx={{ maxHeight: 600 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell><strong>Employee ID</strong></TableCell>
            <TableCell><strong>Name</strong></TableCell>
            <TableCell><strong>Role (Legacy)</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell align="right"><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {userList.map((row) => {
            const isSuperAdmin = isAdminEmail(row.email);
            return (
              <TableRow key={row.uid}>
                <TableCell>{row.hrDetails?.employee_id || 'N/A'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar src={row.hrDetails?.photo_url} sx={{ width: 30, height: 30 }} />
                    <Box>
                      <Typography variant="body2">{row.displayName || 'N/A'}</Typography>
                      <Typography variant="caption" color="textSecondary">{row.email}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={isSuperAdmin ? 'Super Admin' : VALID_ROLES[row.role as keyof typeof VALID_ROLES] || row.role} size="small" />
                </TableCell>
                <TableCell>
                  {row.status === 'invited' ? <Chip label="Invite Pending" size="small" color="warning" /> : 
                   row.hrDetails?.onboarding_status === 'pending_hr_approval' ? <Chip label="Pending Approval" size="small" color="info" /> :
                   <Chip label="Active" size="small" color="success" />}
                </TableCell>
                <TableCell align="right">
                  {!isSuperAdmin && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <IconButton size="small" color="primary" onClick={() => handleOpenHRDialog(row)} title="Review & Edit">
                        {row.hrDetails?.onboarding_status === 'pending_hr_approval' ? <CheckCircleIcon color="info" /> : <EditIcon />}
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteUser(row)} title="Remove User"><DeleteIcon /></IconButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {userList.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>No staff found.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.dark' }}><SecurityIcon fontSize="large" /></Box>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">Staff Management & HR</Typography>
            <Typography variant="subtitle1" color="textSecondary">Manage employee approvals, roles, and payroll</Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setOpenGlobalHRDialog(true)}>Global Leave Settings</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenInviteDialog(true)}>Invite Staff</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={<span>Pending Approvals {pendingApprovals.length > 0 && <Chip size="small" label={pendingApprovals.length} color="error" sx={{ ml: 1, height: 20 }} />}</span>} />
        <Tab label={`Active Staff (${activeStaff.length})`} />
      </Tabs>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        {tabValue === 0 ? renderTable(pendingApprovals) : renderTable(activeStaff)}
      </Paper>

      {/* Invite Dialog */}
      <Dialog open={openInviteDialog} onClose={() => setOpenInviteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Invite Staff Member</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField autoFocus fullWidth label="Staff Email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} margin="normal" variant="outlined" />
          <TextField select fullWidth label="Legacy Role (Fallback)" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} margin="normal" variant="outlined">
            {Object.entries(VALID_ROLES).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenInviteDialog(false)}>Cancel</Button>
          <Button onClick={handleInviteSubmit} variant="contained" color="primary">Send Invite</Button>
        </DialogActions>
      </Dialog>

      {/* HR Verification & Edit Dialog */}
      <Dialog open={openHRDialog} onClose={() => setOpenHRDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          {selectedUser?.hrDetails?.onboarding_status === 'pending_hr_approval' ? 'Review & Approve Employee' : 'Edit Employee Details'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 4 }}>
          <Grid container spacing={3}>
            {/* Left Column: Personal Info Readonly or Editable */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontSize={16} fontWeight="bold" gutterBottom>Personal Details</Typography>
              <Box display="flex" gap={2} mb={2}>
                <Avatar src={hrDetails.photo_url} sx={{ width: 64, height: 64 }} />
                <Box>
                  <Typography variant="body1"><strong>Name:</strong> {selectedUser?.displayName}</Typography>
                  <Typography variant="body2" color="textSecondary"><strong>Gender:</strong> {hrDetails.gender || 'N/A'}</Typography>
                  <Typography variant="body2" color="textSecondary"><strong>Blood:</strong> {hrDetails.blood_group || 'N/A'}</Typography>
                </Box>
              </Box>
              <TextField fullWidth size="small" margin="dense" label="Aadhaar No" value={hrDetails.aadhaar_no || ''} onChange={(e) => setHrDetails({...hrDetails, aadhaar_no: e.target.value})} />
              <TextField fullWidth size="small" margin="dense" label="PAN Number" value={hrDetails.pan_number || ''} onChange={(e) => setHrDetails({...hrDetails, pan_number: e.target.value.toUpperCase()})} />
              <TextField fullWidth size="small" margin="dense" label="Present Address" value={hrDetails.present_address || ''} multiline rows={2} onChange={(e) => setHrDetails({...hrDetails, present_address: e.target.value})} />
              <TextField fullWidth size="small" margin="dense" label="Permanent Address" value={hrDetails.permanent_address || ''} multiline rows={2} onChange={(e) => setHrDetails({...hrDetails, permanent_address: e.target.value})} />
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" fontSize={16} fontWeight="bold" gutterBottom>Work & Payroll</Typography>
              <TextField fullWidth size="small" margin="dense" label="Date of Joining" type="date" InputLabelProps={{ shrink: true }} value={hrDetails.date_of_joining || ''} onChange={(e) => setHrDetails({...hrDetails, date_of_joining: e.target.value})} />
              <TextField fullWidth size="small" margin="dense" label="Designation" value={hrDetails.designation || ''} onChange={(e) => setHrDetails({...hrDetails, designation: e.target.value})} />
              <TextField fullWidth size="small" margin="dense" label="Department" value={hrDetails.department || ''} onChange={(e) => setHrDetails({...hrDetails, department: e.target.value})} />
              <TextField fullWidth size="small" margin="dense" label="Base Salary (₹)" type="number" value={hrDetails.base_salary || ''} onChange={(e) => setHrDetails({...hrDetails, base_salary: parseFloat(e.target.value) || 0})} />
              <TextField fullWidth size="small" margin="dense" label="PF Account Number" value={hrDetails.pf_account_number || ''} onChange={(e) => setHrDetails({...hrDetails, pf_account_number: e.target.value})} />
              <TextField fullWidth size="small" margin="dense" label="UAN" value={hrDetails.uan || ''} onChange={(e) => setHrDetails({...hrDetails, uan: e.target.value})} />
              <TextField fullWidth size="small" margin="dense" label="Bank Account" value={hrDetails.salary_account_number || ''} onChange={(e) => setHrDetails({...hrDetails, salary_account_number: e.target.value})} />
              <TextField fullWidth size="small" margin="dense" label="IFSC Code" value={hrDetails.ifsc_code || ''} onChange={(e) => setHrDetails({...hrDetails, ifsc_code: e.target.value})} />
            </Grid>
            
            {/* Right Column: Module Access */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontSize={16} fontWeight="bold" gutterBottom>Granular Module Access</Typography>
              <Typography variant="body2" color="textSecondary" mb={2}>Select the exact access level for each system module.</Typography>
              
              <Paper variant="outlined" sx={{ p: 2 }}>
                {MODULES.map(mod => (
                  <Box key={mod} display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>{mod}</Typography>
                    <Box display="flex" gap={1}>
                      <Chip label="None" size="small" onClick={() => handleModuleAccessChange(mod, 'none')} color={moduleAccess[mod] === 'none' ? 'error' : 'default'} variant={moduleAccess[mod] === 'none' ? 'filled' : 'outlined'} sx={{ cursor: 'pointer' }} />
                      <Chip label="View" size="small" onClick={() => handleModuleAccessChange(mod, 'view')} color={moduleAccess[mod] === 'view' ? 'info' : 'default'} variant={moduleAccess[mod] === 'view' ? 'filled' : 'outlined'} sx={{ cursor: 'pointer' }} />
                      <Chip label="Edit" size="small" onClick={() => handleModuleAccessChange(mod, 'edit')} color={moduleAccess[mod] === 'edit' ? 'success' : 'default'} variant={moduleAccess[mod] === 'edit' ? 'filled' : 'outlined'} sx={{ cursor: 'pointer' }} />
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenHRDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveHRDetails} variant="contained" color="primary" disabled={savingHR}>
            {savingHR ? 'Saving...' : (selectedUser?.hrDetails?.onboarding_status === 'pending_hr_approval' ? 'Approve Employee & Generate ID' : 'Save Details')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global HR Settings Dialog (Kept almost intact) */}
      <Dialog open={openGlobalHRDialog} onClose={() => setOpenGlobalHRDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SettingsIcon color="primary" />Global HR Settings & Leave Policy</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>Configure global leave policies, weekends off, and company holidays.</Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Annual Leave Quotas</Typography>
              <Box display="flex" flexDirection="column" gap={2} mt={2}>
                <TextField label="Casual Leaves per Year" type="number" value={companySettings.casual_leaves_per_year} onChange={(e) => setCompanySettings({ ...companySettings, casual_leaves_per_year: parseInt(e.target.value) || 0 })} fullWidth />
                <TextField label="Sick Leaves per Year" type="number" value={companySettings.sick_leaves_per_year} onChange={(e) => setCompanySettings({ ...companySettings, sick_leaves_per_year: parseInt(e.target.value) || 0 })} fullWidth />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Weekends Off</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                  <Chip key={day} label={day} onClick={() => handleToggleWeekend(day)} color={companySettings.weekends_off.includes(day) ? "primary" : "default"} variant={companySettings.weekends_off.includes(day) ? "filled" : "outlined"} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Company Holidays</Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <TextField label="Holiday Name" size="small" value={newHolidayName} onChange={(e) => setNewHolidayName(e.target.value)} />
                <LocalizationProvider dateAdapter={AdapterDateFns}><DatePicker label="Select Holiday Date" value={newHoliday} onChange={(newValue) => setNewHoliday(newValue)} slotProps={{ textField: { size: 'small' } }} /></LocalizationProvider>
                <Button variant="contained" onClick={handleAddHoliday} disabled={!newHoliday || !newHolidayName.trim()}>Add Holiday</Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {companySettings.company_holidays.map(holiday => (
                  <Chip key={holiday.date} label={`${holiday.name} (${new Date(holiday.date).toLocaleDateString()})`} onDelete={() => handleRemoveHoliday(holiday.date)} color="secondary" />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenGlobalHRDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveGlobalHR} variant="contained" color="primary" disabled={savingGlobalHR}>{savingGlobalHR ? 'Saving...' : 'Save Global Settings'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StaffManagement;
